// Taken from https://gitlab-ucc.tools.aws.vodafone.com/centrica-amazon-connect/applications/admin-portal/vgsl-cen-s3web-adminportal/-/blob/development/src/components/Shared/Forms/MultiSelectInput/

import Checkbox from "@vfuk/core-checkbox";
import Paragraph from "@vfuk/core-paragraph";
import Spacing from "@vfuk/core-spacing";
import Span from "@vfuk/core-span";
import Tag from "@vfuk/core-tag";
import {
  type ComponentProps,
  forwardRef,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";
import Select, {
  type CommonProps,
  components,
  type ContainerProps,
  type InputActionTypes,
  type OptionProps,
} from "react-select";
import type { SelectComponentsProps } from "react-select/src/Select";
import SelectInput, { type SelectOption } from "../SelectInput/SelectInput";
import { Stack } from "../Stack";
import classes from "./MultiSelectInput.module.scss";

type IsMulti = true;

type LocalInputProps = ComponentProps<typeof components.Input> &
  CommonProps<SelectOption, IsMulti>;

const SelectContainer = ({
  children,
  ...props
}: ContainerProps<SelectOption, IsMulti>) => {
  const chosenOptions = props.getValue();

  const handleOptionRemoved = (deletedOption: SelectOption) => {
    props.setValue(
      chosenOptions.filter((option) => option.value !== deletedOption.value),
      "deselect-option"
    );
  };

  const isSelectionMade = chosenOptions.length > 0;

  return (
    <Spacing spacingLevel={{ bottom: props.selectProps.spaceForError ? 6 : 0 }}>
      <Spacing
        spacingLevel={{
          bottom:
            props.selectProps.showChosenOptions && isSelectionMade ? 2 : 0,
        }}
      >
        <components.SelectContainer {...props}>
          {children}
        </components.SelectContainer>
      </Spacing>
      {props.selectProps.showSelectedCount && (
        <>
          {isSelectionMade ? (
            <Spacing spacingLevel={{ bottom: 2 }}>
              <Paragraph size={2} noMargin>
                {props.selectProps.selectionLimit > 0 ? (
                  <Span appearance="alt2">
                    <Trans
                      i18nKey={"shared:forms:input:multi_select:select_limit"}
                      values={{
                        selected: chosenOptions.length,
                        total: props.selectProps.selectionLimit,
                      }}
                    />
                  </Span>
                ) : (
                  <Span appearance="alt2">
                    <Trans
                      i18nKey={"shared:forms:input:multi_select:select_count"}
                      values={{
                        selected: chosenOptions.length,
                      }}
                    />
                  </Span>
                )}
              </Paragraph>
            </Spacing>
          ) : (
            <div className={classes.SelectedCount} />
          )}
        </>
      )}

      {props.selectProps.showChosenOptions && (
        <Stack direction="row" flexWrap="wrap">
          {isSelectionMade ? (
            chosenOptions.map((option) => (
              <Spacing key={option.label as string} spacingLevel={{ right: 2 }}>
                <Tag
                  text={option.label as string}
                  onClick={() => handleOptionRemoved(option)}
                  appearance="primary"
                />
              </Spacing>
            ))
          ) : (
            <div className={classes.SelectedOptions} />
          )}
        </Stack>
      )}
    </Spacing>
  );
};

type MultiSelectInputProps = {
  id: string;
  label: string;
  subtext?: string;
  showSelectedCount?: boolean;
  spaceForError?: boolean;
  showChosenOptions?: boolean;
  isDisabled?: boolean;
  selectionLimit?: number;
  isSelectAll?: boolean;
} & SelectComponentsProps;

const MultiSelectInput = forwardRef<Select, MultiSelectInputProps>(
  (
    {
      showChosenOptions = true,
      showSelectedCount = false,
      spaceForError = true,
      selectionLimit = 0,
      isSelectAll = false,
      ...props
    }: MultiSelectInputProps,
    ref
  ): JSX.Element => {
    const [selectInput, setSelectInput] = useState<string>("");
    const isAllSelected = useRef<boolean>(false);
    const selectAllLabel = useRef<string>("Select all");
    const allOption = { value: "*", label: selectAllLabel.current };

    const filterOptions = (options: SelectOption[], input: string) =>
      options?.filter(({ label }: SelectOption) =>
        `${label}`.toLowerCase().includes(input.toLowerCase())
      );

    const comparator = (a: SelectOption, b: SelectOption) =>
      `${a.value}`.localeCompare(`${b.value}`);

    const filteredOptions = filterOptions(props.options, selectInput);
    const filteredSelectedOptions = filterOptions(
      props.value || [],
      selectInput
    );

    const Option = ({
      children,
      ...props
    }: OptionProps<SelectOption, IsMulti>) => (
      <components.Option {...props}>
        <div className={classes.OptionCheckBox}>
          {props.data.value === "*" &&
          !isAllSelected.current &&
          filteredSelectedOptions?.length > 0 ? (
            <Checkbox
              checked={props.isSelected}
              onChange={() => {
                return;
              }}
              name={""}
              id={""}
              value={""}
            >
              {null}
            </Checkbox>
          ) : (
            <Checkbox
              checked={props.isSelected || isAllSelected.current}
              onChange={() => {
                return;
              }}
              name={""}
              id={""}
              value={""}
            >
              {null}
            </Checkbox>
          )}
        </div>
        {children}
      </components.Option>
    );

    const Input = (props: LocalInputProps) => (
      <>
        {selectInput.length === 0 ? (
          <components.Input
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            autoFocus={props.selectProps.menuIsOpen}
            {...props}
          />
        ) : (
          <div>
            <components.Input
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              autoFocus={props.selectProps.menuIsOpen}
              {...props}
            />
          </div>
        )}
      </>
    );

    const customFilterOption = (
      { value, label }: SelectOption,
      input: string
    ) =>
      (value !== "*" &&
        `${label}`.toLowerCase().includes(input.toLowerCase())) ||
      (value === "*" && filteredOptions?.length > 0);

    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if ((e.key === " " || e.key === "Enter") && !selectInput)
        e.preventDefault();
    };

    const onInputChange = (
      inputValue: string,
      event: { action: InputActionTypes }
    ) => {
      if (event.action === "input-change") setSelectInput(inputValue);
      else if (event.action === "menu-close" && selectInput !== "")
        setSelectInput("");
    };

    const handleChange = (selected: SelectOption[]) => {
      if (
        selected.length > 0 &&
        !isAllSelected.current &&
        (selected[selected.length - 1].value === allOption.value ||
          JSON.stringify([...filteredOptions].sort(comparator)) ===
            JSON.stringify([...selected].sort(comparator)))
      ) {
        return props.onChange(
          [
            ...(props.value ?? []),
            ...props.options.filter(
              ({ label }: SelectOption) =>
                `${label}`.toLowerCase().includes(selectInput?.toLowerCase()) &&
                (props.value ?? []).filter(
                  (opt: SelectOption) => opt.label === label
                ).length === 0
            ),
          ].sort(comparator)
        );
      } else if (
        selected.length > 0 &&
        selected[selected.length - 1].value !== allOption.value &&
        JSON.stringify([...selected].sort(comparator)) !==
          JSON.stringify([...filteredOptions].sort(comparator))
      ) {
        return props.onChange(selected);
      } else if (
        selected.length > 0 &&
        selected[selected.length - 1].value === allOption.value &&
        JSON.stringify([...filteredOptions].sort(comparator)) ===
          JSON.stringify(
            [...selected.filter(({ value }) => value !== allOption.value)].sort(
              comparator
            )
          )
      ) {
        return props.onChange([]);
      }

      return props.onChange([
        ...(props.data.value?.filter(
          ({ label }: SelectOption) =>
            !`${label}`.toLowerCase().includes(selectInput?.toLowerCase())
        ) || []),
      ]);
    };

    const componentsList = useMemo(
      () => ({
        SelectContainer,
        Option,
        Input,
      }),
      [selectionLimit, Option, Input]
    );

    if (isSelectAll && props.options.length !== 0) {
      isAllSelected.current =
        JSON.stringify([...filteredSelectedOptions].sort(comparator)) ===
        JSON.stringify([...filteredOptions].sort(comparator));

      if (filteredSelectedOptions?.length > 0) {
        if (filteredSelectedOptions?.length === filteredOptions?.length)
          selectAllLabel.current = `All (${filteredOptions.length}) selected`;
        else
          selectAllLabel.current = `${filteredSelectedOptions?.length} / ${filteredOptions.length} selected`;
      } else selectAllLabel.current = "Select all";

      allOption.label = selectAllLabel.current;

      return (
        <SelectInput
          components={componentsList}
          isClearable={false}
          isMulti
          menuPortalTarget={document.body}
          controlShouldRenderValue={false}
          backspaceRemovesValue={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          showSelectedCount={showSelectedCount}
          spaceForError={spaceForError}
          selectionLimit={selectionLimit}
          showChosenOptions={showChosenOptions}
          onKeyDown={onKeyDown}
          onInputChange={onInputChange}
          {...props}
          onChange={handleChange}
          filterOption={customFilterOption}
          styles={{ ...props.styles }}
          ref={ref}
          inputValue={selectInput}
          options={[allOption, ...props.options]}
          menuPlacement={props.menuPlacement ?? "auto"}
          tabSelectsValue={false}
          blurInputOnSelect={false}
        />
      );
    }

    return (
      <SelectInput
        components={componentsList}
        isClearable={false}
        isMulti
        menuPortalTarget={document.body}
        controlShouldRenderValue={false}
        backspaceRemovesValue={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        showSelectedCount={showSelectedCount}
        spaceForError={spaceForError}
        selectionLimit={selectionLimit}
        showChosenOptions={showChosenOptions}
        {...props}
        styles={{ ...props.styles }}
        ref={ref}
      />
    );
  }
);

MultiSelectInput.displayName = "MultiSelectInput";

export default MultiSelectInput;
